import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { LucideIcon, ChevronDown } from "lucide-react";

interface TimePickerProps {
    name?: string; value: string; error?: string; label?: string;
    onChange: (value: string) => void;
    icon?: LucideIcon;
}

const ROW_HEIGHT = 32;
const VISIBLE_ROWS = 5;
const CENTER_INDEX = Math.floor(VISIBLE_ROWS / 2);
const hours = Array.from({ length: 12 }, (_, i) => i + 1);
const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));
const periods = ["AM", "PM"] as const;

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, error, icon: Icon, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const [inputValue, setInputValue] = useState(value || "");
    
    const triggerRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);

    const [hour, setHour] = useState("9");
    const [minute, setMinute] = useState("15");
    const [period, setPeriod] = useState<"AM" | "PM">("AM");

    const hourRef = useRef<HTMLDivElement>(null);
    const minuteRef = useRef<HTMLDivElement>(null);
    const wasOpen = useRef(false);

    useEffect(() => {
        setInputValue(value || "");
        if (value) {
            const match = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (match) {
                setHour(parseInt(match[1], 10).toString());
                setMinute(match[2]);
                setPeriod(match[3].toUpperCase() as "AM" | "PM");
            }
        }
    }, [value]);

    const openPicker = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setCoords({ top: rect.bottom + 8, left: rect.left, width: rect.width });
            setIsOpen(true);
        }
    };

    // Snap when opened, then follow typed values smoothly.
    useEffect(() => {
        const behavior = wasOpen.current ? "smooth" : "auto";
        wasOpen.current = isOpen;
        if (isOpen) {
            const frame = requestAnimationFrame(() => {
                const activeHour = hourRef.current?.querySelector('[data-selected="true"]');
                const activeMinute = minuteRef.current?.querySelector('[data-selected="true"]');
                
                if (activeHour) activeHour.scrollIntoView({ block: "center", behavior });
                if (activeMinute) activeMinute.scrollIntoView({ block: "center", behavior });
            });
            return () => cancelAnimationFrame(frame);
        }
    }, [isOpen, hour, minute, period]);

    useEffect(() => {
        const handleInteraction = (e: Event) => {
            if (portalRef.current && portalRef.current.contains(e.target as Node)) return;
            if (e.type === 'mousedown' && triggerRef.current && triggerRef.current.contains(e.target as Node)) return; 
            setIsOpen(false);
        };

        if (isOpen) {
            window.addEventListener('scroll', handleInteraction, true);
            window.addEventListener('resize', handleInteraction);
            document.addEventListener('mousedown', handleInteraction);
        }
        return () => {
            window.removeEventListener('scroll', handleInteraction, true);
            window.removeEventListener('resize', handleInteraction);
            document.removeEventListener('mousedown', handleInteraction);
        };
    }, [isOpen]); 

    const parseManualTime = (input: string): string | null => {
        const text = input.trim().toLowerCase();
        if (!text) return null;

        let h = 0, m = 0, p = "";
        const match = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(a|p)?m?$/);

        if (match) {
            h = parseInt(match[1], 10);
            m = match[2] ? parseInt(match[2], 10) : 0;
            p = match[3] || "";
        } else {
            const digits = text.replace(/[^0-9]/g, '');
            if (digits.length === 3 || digits.length === 4) {
                h = parseInt(digits.slice(0, -2), 10);
                m = parseInt(digits.slice(-2), 10);
                p = text.replace(/[^a-p]/g, '');
            } else if (digits.length === 1 || digits.length === 2) {
                h = parseInt(digits, 10);
                m = 0;
                p = text.replace(/[^a-p]/g, '');
            } else {
                return null;
            }
        }

        if (h > 23 || m > 59) return null;

        const isPM = p === 'p' || (h >= 12 && p !== 'a');
        if (h > 12) h -= 12;
        if (h === 0) h = 12;

        return `${h}:${m.toString().padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;
    };

    const handleBlur = () => {
        const parsed = parseManualTime(inputValue);
        if (parsed) {
            setInputValue(parsed);
            if (parsed !== value) {
                onChange(parsed);
            }
        } else {
            setInputValue(value); 
        }
    };

    const updateTime = (h: string, m: string, p: "AM" | "PM") => onChange(`${h}:${m} ${p}`);

    return (
        <div className="flex flex-col gap-1.5 w-full">
            {label && <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest ml-1">{label}</label>}

            <div
                ref={triggerRef}
                className={`relative flex items-center h-12 bg-gray-50/50 border rounded-xl transition-all duration-200
                    ${error ? "border-red-300 bg-red-50/30" : isOpen ? "border-[#7E57C2] ring-4 ring-[#7E57C2]/10 bg-white" : "border-gray-200 hover:border-gray-300"}
                `}
            >
                {Icon && <Icon size={18} className={`absolute left-3.5 transition-colors ${isOpen ? "text-[#7E57C2]" : "text-gray-400"}`} />}
                
                <input
                    type="text"
                    value={inputValue}
                    placeholder="Select Time"
                    onClick={() => !isOpen && openPicker()}
                    onChange={(e) => {
                        const val = e.target.value;
                        setInputValue(val);
                        
                        // 3. Parse while typing and update internal picker state for the rotation effect
                        const parsed = parseManualTime(val);
                        if (parsed) {
                            const match = parsed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                            if (match) {
                                setHour(parseInt(match[1], 10).toString());
                                setMinute(match[2]);
                                setPeriod(match[3].toUpperCase() as "AM" | "PM");
                            }
                        }
                    }}
                    onBlur={handleBlur}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.currentTarget.blur();
                            setIsOpen(false);
                        }
                    }}
                    // 4. Added focus:outline-none focus:ring-0 to kill the default browser styling
                    className="flex-1 text-sm md:text-base text-gray-900 font-medium bg-transparent border-none outline-none focus:outline-none focus:ring-0 shadow-none pl-11 pr-10 w-full truncate"
                />

                <div 
                    className="absolute right-3 p-1 cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(!isOpen);
                        if (!isOpen) openPicker();
                    }}
                >
                    <ChevronDown size={18} className={`text-gray-400 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#7E57C2]" : ""}`} />
                </div>
            </div>

            {isOpen && ReactDOM.createPortal(
                <div 
                    ref={portalRef}
                    className="time-picker-popup dark-popup fixed z-[999999] p-3 bg-white border border-gray-100 rounded-xl shadow-2xl select-none animate-in fade-in slide-in-from-top-2"
                    style={{ top: coords.top, left: coords.left, width: Math.max(coords.width, 240) }}
                >
                    <div className="flex items-center h-[160px] justify-center">
                        <div ref={hourRef} className="flex flex-col h-full overflow-y-auto no-scrollbar w-16 snap-y snap-mandatory">
                            <div style={{ height: `${CENTER_INDEX * ROW_HEIGHT}px` }} className="shrink-0" />
                            {hours.map((h) => (
                                <div 
                                    key={`h-${h}`} 
                                    data-selected={hour === h.toString()}
                                    onClick={(e) => { e.stopPropagation(); setHour(h.toString()); updateTime(h.toString(), minute, period); }}
                                    className={`flex items-center justify-center h-8 shrink-0 text-sm transition-all cursor-pointer rounded-lg snap-center mx-1 ${hour === h.toString() ? "text-[#7E57C2] font-bold bg-[#F9F6FF]" : "text-gray-500 hover:bg-gray-50"}`}>
                                    {h}
                                </div>
                            ))}
                            <div style={{ height: `${CENTER_INDEX * ROW_HEIGHT}px` }} className="shrink-0" />
                        </div>
                        <div className="text-xl font-bold text-gray-300 pb-1">:</div>
                        <div ref={minuteRef} className="flex flex-col h-full overflow-y-auto no-scrollbar w-16 snap-y snap-mandatory">
                            <div style={{ height: `${CENTER_INDEX * ROW_HEIGHT}px` }} className="shrink-0" />
                            {minutes.map((m) => (
                                <div 
                                    key={`m-${m}`} 
                                    data-selected={minute === m}
                                    onClick={(e) => { e.stopPropagation(); setMinute(m); updateTime(hour, m, period); }}
                                    className={`flex items-center justify-center h-8 shrink-0 text-sm transition-all cursor-pointer rounded-lg snap-center mx-1 ${minute === m ? "text-[#7E57C2] font-bold bg-[#F9F6FF]" : "text-gray-500 hover:bg-gray-50"}`}>
                                    {m}
                                </div>
                            ))}
                            <div style={{ height: `${CENTER_INDEX * ROW_HEIGHT}px` }} className="shrink-0" />
                        </div>
                        <div className="flex flex-col justify-center border-l border-gray-100 pl-4 pr-2 gap-2">
                            {periods.map((p) => (
                                <button key={p} type="button" onClick={(e) => { e.stopPropagation(); setPeriod(p); updateTime(hour, minute, p); }}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${period === p ? "bg-[#7E57C2] text-white shadow-md shadow-[#7E57C2]/20" : "text-gray-400 hover:bg-gray-100"}`}>
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {error && <div className="mt-1 ml-1 flex items-center gap-1"><span className="text-[11px] font-medium text-red-500 tracking-tight">{error}</span></div>}
        </div>
    );
};

export default TimePicker;
